
import { cn } from "../../lib/utils";

interface Column<T> {
    header: string;
    accessor: (item: T) => React.ReactNode;
    className?: string;
}

interface Props<T> {
    data: T[];
    columns: Column<T>[];
    keyField: keyof T;
    onRowClick?: (item: T) => void;
    loading?: boolean;
    emptyMessage?: string;
}

const columnCanSizeItself = (className = '') => (
    className.includes('w-') ||
    className.includes('min-w-') ||
    className.includes('max-w-') ||
    className.includes('overflow-visible')
);

export function CompactTable<T>({ data, columns, keyField, onRowClick, loading, emptyMessage }: Props<T>) {
    return (
        <div className="overflow-x-auto rounded-md border bg-card text-card-foreground shadow-sm text-xs">
            <table className="w-full text-left caption-bottom">
                <thead className="bg-muted/50 border-b">
                    <tr>
                        {columns.map((col, idx) => {
                            const className = col.className || '';
                            const canSizeItself = columnCanSizeItself(className);
                            return (
                                <th
                                    key={idx}
                                    className={cn("px-2 py-1 font-medium text-muted-foreground align-middle text-xs uppercase tracking-wider", col.className)}
                                    style={{
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        minWidth: canSizeItself ? undefined : 60,
                                        maxWidth: canSizeItself ? undefined : 180,
                                        whiteSpace: 'nowrap',
                                        overflow: canSizeItself ? 'visible' : 'hidden',
                                        textOverflow: canSizeItself ? 'clip' : 'ellipsis',
                                    }}
                                >
                                    {col.header}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {loading ? (
                        <tr>
                            <td colSpan={columns.length} className="px-2 py-6 text-center text-muted-foreground italic text-xs">
                                Loading...
                            </td>
                        </tr>
                    ) : (
                        <>
                            {data.map((item, rowIndex) => {
                                const rawKey = item[keyField];
                                const rowKey = rawKey !== undefined && rawKey !== null && String(rawKey).trim() !== ''
                                    ? String(rawKey)
                                    : `${String(keyField)}-${rowIndex}`;
                                return (
                                <tr
                                    key={rowKey}
                                    onClick={() => onRowClick && onRowClick(item)}
                                    className={cn(
                                        "hover:bg-muted/50 transition-colors data-[state=selected]:bg-muted",
                                        onRowClick ? "cursor-pointer" : ""
                                    )}
                                >
                                    {columns.map((col, cIdx) => {
                                        const className = col.className || '';
                                        const canSizeItself = columnCanSizeItself(className);
                                        return (
                                            <td
                                                key={cIdx}
                                                className={cn("px-2 py-1 align-middle", col.className)}
                                                style={{
                                                    fontSize: '12px',
                                                    padding: '4px 8px',
                                                    minWidth: canSizeItself ? undefined : 60,
                                                    maxWidth: canSizeItself ? undefined : 120,
                                                    whiteSpace: 'nowrap',
                                                    overflow: canSizeItself ? 'visible' : 'hidden',
                                                    textOverflow: canSizeItself ? 'clip' : 'ellipsis',
                                                }}
                                            >
                                                {col.accessor(item)}
                                            </td>
                                        );
                                    })}
                                </tr>
                                );
                            })}
                            {data.length === 0 && (
                                <tr>
                                    <td colSpan={columns.length} className="px-2 py-6 text-center text-muted-foreground italic text-xs">
                                        {emptyMessage || "No records found."}
                                    </td>
                                </tr>
                            )}
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
}
